import fnNoOp from 'lib/util/no-op';
import { useEffect } from 'react';

/**
 * A custom React hook that listens for the Konami Code sequence of key presses
 * and triggers a callback function when the sequence is successfully entered.
 *
 * @param onCodeEntered - A callback function to be executed when the Konami Code is entered.
 * @param isActive - A boolean flag indicating whether the hook should be active.
 *
 * @remarks
 * The Konami Code sequence is as follows:
 * ArrowUp, ArrowUp, ArrowDown, ArrowDown, ArrowLeft, ArrowRight, ArrowLeft, ArrowRight, KeyB, KeyA.
 *
 * When the `isActive` parameter is `false`, the hook will not listen for key presses.
 * The hook automatically cleans up the event listener when the component unmounts or when `isActive` changes.
 *
 * @example
 * ```tsx
 * const App = () => {
 *   const handleKonamiCode = () => {
 *     logInfo('Konami Code entered!');
 *   };
 *
 *   useKonamiCode(handleKonamiCode, true);
 *
 *   return <div>Press the Konami Code!</div>;
 * };
 * ```
 */
const useKonamiCode = (onCodeEntered: () => void, isActive: boolean) => {
  useEffect(() => {
    if (!isActive) {
      return fnNoOp;
    }

    const konamiCode = [
      'ArrowUp',
      'ArrowUp',
      'ArrowDown',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'ArrowLeft',
      'ArrowRight',
      'KeyB',
      'KeyA',
    ];
    let konamiIndex = 0;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === konamiCode[konamiIndex]) {
        konamiIndex++;
        if (konamiIndex === konamiCode.length) {
          onCodeEntered();
          konamiIndex = 0;
        }
      } else {
        konamiIndex = 0;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCodeEntered, isActive]);
};

export default useKonamiCode;
